import React, { useCallback, useEffect, useState } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  Node,
  Edge,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Card, Button, Row, Col } from "react-bootstrap";
import { NetworkUser } from "./hooks/use-network";
import { apiClient } from "../../services/api";
import { getLayoutedElements } from "../../utils/layout";
import { getRankColor } from "../../utils/getRankColor";
import { getRankImage } from "../../utils/getRankImage";

interface CustomNodeData {
  name: string;
  email: string;  
  code: string;
  last_rank: string;
  isRoot: Boolean;
  isActive: boolean;
  onInfoClick: () => void;
  onNetworkClick: () => void;
  hasChildren: boolean;
}

interface NetworkVisualizationProps {
  teamMembers: NetworkUser[];
  currentUserId?: string;
  userData?: any;
  onViewNetwork: (memberId: string) => void;
  isLoading?: boolean;
  onClose?: () => void;
}

const CustomNode: React.FC<{ data: CustomNodeData }> = ({ data }) => {
  const {
    code,
    email,
    last_rank,
    isRoot,
    isActive,
    onInfoClick,
    onNetworkClick,
    hasChildren,
    name
  } = data;

  const borderColor = getRankColor(last_rank);

  if (isRoot) {
    return (
      <>
        <Handle type="source" position={Position.Bottom} />
        <div
          style={{
            width: 350,
            height: 155,
            borderRadius: 18,
            background: "#0E1215",
            border: `1px solid ${borderColor}`,
            boxShadow: `0 0 20px ${borderColor}55`,
            display: "flex",
            alignItems: "center",
            padding: "0 18px",
            gap: 15,
          }}
        >
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              border: `3px solid ${borderColor}`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxShadow: `0 0 15px ${borderColor}55`,
            }}
          >
            <img
              src={getRankImage(last_rank || "")}
              alt={last_rank || ""}
              style={{
                width: 80,
                height: 80,
                objectFit: "contain",
                filter: `drop-shadow(0 0 10px ${borderColor})`,
              }}
            />
          </div>

          <div>
            <div
              style={{
                color: "#FFF",
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              Tu
            </div>
            <div
              style={{
                color: "#FFF",
                fontSize: 22,
                fontWeight: 700,
              }}
            >
              {code}
            </div>
            <div
              style={{
                background: borderColor,
                borderRadius: 20,
                display: "inline-block",
                padding: "3px 10px",
                color: "#FFF",
                fontSize: 11,
                marginTop: 5,
              }}
            >
              {last_rank}
            </div>

            <div
              style={{
                color: "#C1C7D0",
                fontSize: 18,
                marginTop: 4,
              }}
            >
              Nivel 0
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Handle type="target" position={Position.Top} />

      <div
        style={{
          width: 160,
          height: 190,
          borderRadius: 16,
          background: "#0E1215",
          border: `1px solid ${borderColor}`,
          boxShadow: `0 0 20px ${borderColor}55`,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 15,
        }}
        onClick={() => {
          onInfoClick();
        }}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: `2px solid ${borderColor}`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "#101b34",
            marginBottom: 12,
          }}
        >
          <img
            src={getRankImage(last_rank || "")}
            alt={last_rank || ""}
            style={{
              width: 50,
              height: 50,
              objectFit: "contain",
              filter: `drop-shadow(0 0 10px ${borderColor})`,
            }}
          />
        </div>


        <div
          style={{
            color: "#fff",
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          {code}
        </div>
        <div
          style={{
            color: "#fff",
            fontSize: 8,
          }}
        >
          {name}
        </div>
        <div
          style={{
            color: "#fff",
            fontSize: 8,
            marginBottom: 6,
          }}
        >
          {email}
        </div>
        <div
          style={{
            marginTop: 10,
            background: borderColor,
            padding: "3px 10px",
            borderRadius: 20,
            color: "#fff",
            fontSize: 10,
            fontWeight: 600,
          }}
        >
          {last_rank}
        </div>

        <div
          style={{
            marginTop: 10,
            color: "#fff",
            fontSize: 12,
          }}
        >
          Nivel 1
        </div>

        {isActive ? (
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#32D74B",
              }}
            />
            <span
              style={{
                color: "#DDD",
                fontSize: 12,
              }}
            >
              Activo
            </span>
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#FF453A",
              }}
            />
            <span
              style={{
                color: "#DDD",
                fontSize: 12,
              }}
            >
              Inactivo
            </span>
          </div>
        )}
      </div>

      <div
        style={{
          position: "absolute",
          bottom: -20,
          left: "30%",
          transform: "translateX(-50%)",
        }}
      >
        {hasChildren && (
          <i
            className="ri-node-tree"
            style={{ cursor: "pointer", fontSize: 20 }}
            onClick={onNetworkClick}
            title="Ver red"
          />
        )}
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};

const NetworkVisualization: React.FC<NetworkVisualizationProps> = ({
  teamMembers,
  userData,
  isLoading = false,
  onClose,
}) => {
  const [modalData, setModalData] = useState<NetworkUser | null>(null);
  const [loadedParents, setLoadedParents] = useState(new Set<string>());

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const nodeTypes = {
    custom: CustomNode,
  };

  const onLayout = useCallback(
    (nodesToLayout: Node[], edgesToLayout: Edge[]) => {
      const { nodes: layoutedNodes, edges: layoutedEdges } =
        getLayoutedElements(nodesToLayout, edgesToLayout);
      setNodes(layoutedNodes);
      setEdges(layoutedEdges);
    },
    [setNodes, setEdges],
  );

  const toggleSubNetwork = useCallback(
    async (parentId: string) => {
      if (loadedParents.has(parentId)) {
        // Collapse logic
        const childrenIdsToRemove = new Set();
        const queue = [parentId];
        const visited = new Set([parentId]);

        while (queue.length > 0) {
          const currentId = queue.shift();
          const children = edges
            .filter((e) => e.source === currentId)
            .map((e) => e.target);
          for (const childId of children) {
            if (!visited.has(childId)) {
              childrenIdsToRemove.add(childId);
              queue.push(childId);
              visited.add(childId);
            }
          }
        }

        const remainingNodes = nodes.filter(
          (n) => !childrenIdsToRemove.has(n.id),
        );
        const remainingEdges = edges.filter(
          (e) => !childrenIdsToRemove.has(e.target),
        );

        setLoadedParents((prev) => {
          const newSet = new Set(prev);
          childrenIdsToRemove.forEach((id: any) => newSet.delete(id));
          newSet.delete(parentId);
          return newSet;
        });

        onLayout(remainingNodes, remainingEdges);
        return;
      }

      try {
        const response = await apiClient.get(
          `/users/admin/network/with/id?userid=${parentId}`,
          {
            headers: {
              Authorization: `${localStorage.getItem("token") || ""}`,
              Accept: "application/json",
            },
          },
        );

        const referrals = response?.data?.data?.user ?? [];

        const newNodes = referrals
          .filter((ref: any) => !nodes.some((n) => n.id === ref._id))
          .map((ref: any) => ({
            id: ref._id,
            type: "custom",
            position: { x: 0, y: 0 }, // Dagre will calculate this
            data: {
              name: ref.name,
              email: ref.email,
              code: ref.multyhealth_code,
              last_rank: ref.last_rank?.rank.name ?? "",
              isRoot: false,
              isActive: ref.network?.loyalty_status === true,
              hasChildren: ref.referenced_by_count > 0,
              onInfoClick: () => {
                setModalData(ref);
              },
              onNetworkClick: () => toggleSubNetwork(ref._id),
            },
          }));

        const newEdges = referrals
          .filter(
            (ref: any) =>
              !edges.some((e) => e.source === parentId && e.target === ref._id),
          )
          .map((ref: any) => ({
            id: `e-${parentId}-${ref._id}`,
            source: parentId,
            target: ref._id,
          }));

        setNodes((currentNodes) => {
          const updatedNodes = [...currentNodes, ...newNodes];
          setEdges((currentEdges) => {
            const updatedEdges = [...currentEdges, ...newEdges];
            onLayout(updatedNodes, updatedEdges);
            return updatedEdges;
          });
          return updatedNodes;
        });

        setLoadedParents((prev) => new Set(prev).add(parentId));
      } catch (error) {
        console.error("Error loading sub network:", error);
      }
    },
    [
      loadedParents,
      setLoadedParents,
      nodes,
      edges,
      onLayout,
      setEdges,
      setNodes,
    ],
  );

  // Remove light class from divs inside network-visualization
  useEffect(() => {
    const networkContainer = document.getElementById("network-visualization");
    if (networkContainer) {
      const divsWithLightClass = networkContainer.querySelectorAll("div.light");
      divsWithLightClass.forEach((div) => {
        div.classList.remove("light");
      });
    }
  }, []);

  // Initialize the visualization with current user and team members
  useEffect(() => {
    if (!userData || !teamMembers || nodes.length > 0) return;

    setModalData(userData);

    const mainNode: Node = {
      id: userData.id,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        name: userData.name,
        email: userData.email,
        code: userData.multyhealth_code,
        last_rank: userData.last_rank?.rank.name ?? "",
        isActive: userData.network?.loyalty_status === true,
        isRoot: true,
        hasChildren: teamMembers.length > 0,
        onNetworkClick: () => toggleSubNetwork(userData.id),
      },
    };

    const memberNodes: Node[] = teamMembers.map((member) => ({
      id: member.id,
      type: "custom",
      position: { x: 0, y: 0 },
      data: {
        name: member.name,
        email: member.email,
        code: member.multyhealth_code,
        last_rank: member.last_rank?.rank.name ?? "",
        isRoot: false,
        isActive: member.network?.loyalty_status === true,
        hasChildren: member.referenced_by_count > 0,
        onInfoClick: () => {
          setModalData(member);
        },
        onNetworkClick: () => toggleSubNetwork(member.id),
      },
    }));

    const memberEdges: Edge[] = teamMembers.map((member) => ({
      id: `e-${userData.id}-${member.id}`,
      source: userData.id,
      target: member.id,
    }));

    const initialNodes = [mainNode, ...memberNodes];
    const initialEdges = memberEdges;

    onLayout(initialNodes, initialEdges);

    if (teamMembers.length > 0) {
      setLoadedParents(new Set([userData.id]));
    }
  }, [userData, teamMembers, nodes.length, onLayout, toggleSubNetwork]);

  if (isLoading) {
    return (
      <Card className="border-0 rounded-3 p-4 mb-4">
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Cargando visualización...</span>
          </div>
          <p className="mt-2 mb-0">Cargando visualización de red...</p>
        </div>
      </Card>
    );
  }

  const borderColor = getRankColor(modalData?.last_rank?.rank.name ?? "");

  return (
    <>
      <Card className="border-0 rounded-3 p-4 mb-4 ">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2">
            {onClose && (
              <Button variant="outline-primary" size="sm" onClick={onClose}>
                <i className="ri-arrow-left-line me-1"></i>
                Volver
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                // Reset visualization
                setNodes([]);
                setEdges([]);
                setLoadedParents(new Set());
              }}
            >
              <i className="ri-refresh-line me-1"></i>
              Reiniciar
            </Button>
          </div>
        </div>
        <Row>
          <Col md={9}>
            <div
              id="network-visualization"
              style={{
                height: "65vh",
                borderRadius: "8px",
              }}
            >
              <ReactFlow
                nodeTypes={nodeTypes}
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                fitView
                fitViewOptions={{ padding: 0.2 }}
              >
                <Controls position="top-left" />
                <Background />
              </ReactFlow>
            </div>
          </Col>

          <Col md={3}>
            {modalData && (
              <Card
                className="border-0"
                style={{
                  borderRadius: 20,
                  minHeight: "65vh",
                  boxShadow: "0 0 40px rgba(0,0,0,.4)",
                }}
              >
                <Card.Body className="px-4 py-4">
                  <div
                    style={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      marginBottom: 35,
                    }}
                  >
                    Información del usuario
                  </div>

                  <>
                    <div className="text-center">
                      <div
                        className="mx-auto mb-3 d-flex justify-content-center align-items-center"
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: "50%",
                          border: `3px solid ${getRankColor(
                            modalData.last_rank?.rank.name,
                          )}`,
                          boxShadow: `0 0 20px ${getRankColor(
                            modalData.last_rank?.rank.name,
                          )}55`,
                        }}
                      >
                        <img
                          src={getRankImage(modalData.last_rank?.rank.name)}
                          alt={modalData.last_rank?.rank.name || ""}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "contain",
                            filter: `drop-shadow(0 0 10px ${borderColor})`,
                          }}
                        />
                      </div>

                      <div
                        style={{
                          color: "#FFF",
                          fontWeight: 700,
                          fontSize: 28,
                        }}
                      >
                        {modalData.multyhealth_code}
                      </div>

                      <div
                        style={{
                          color: "#C1C7D0",
                          fontSize: 14,
                          marginBottom: 12,
                        }}
                      >
                        {modalData.name}({modalData.email})
                      </div>

                      <div
                        style={{
                          background: getRankColor(
                            modalData.last_rank?.rank.name,
                          ),
                          display: "inline-block",
                          padding: "5px 14px",
                          borderRadius: 20,
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {modalData.last_rank?.rank.name}
                      </div>

                      <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                        {modalData?.network.loyalty_status ? (
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#32D74B",
                              }}
                            />
                            <span
                              style={{
                                color: "#DDD",
                                fontSize: 12,
                              }}
                            >
                              Activo
                            </span>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "#FF453A",
                              }}
                            />
                            <span
                              style={{
                                color: "#DDD",
                                fontSize: 12,
                              }}
                            >
                              Inactivo
                            </span>
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          color: "#AAA",
                          fontSize: 13,
                          marginTop: 10,
                        }}
                      >
                        Nivel 1
                      </div>
                    </div>

                    <hr
                      style={{
                        borderColor: "#1D2530",
                        marginTop: 20,
                        marginBottom: 20,
                      }}
                    />

                    <div className="d-flex flex-column gap-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-calendar-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>
                            Fecha de registro
                          </span>
                        </div>

                        <span style={{ color: "#FFF", fontWeight: 500 }}>
                          {new Date(
                            modalData?.createdAt ?? "",
                          ).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-bar-chart-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>
                            Puntos de regalias mes actual
                          </span>
                        </div>

                        <span style={{ color: "#FFF", fontWeight: 500 }}>
                          {modalData?.monthPoints?.toLocaleString()}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-team-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>Usuarios</span>
                        </div>

                        <span style={{ color: "#FFF", fontWeight: 500 }}>
                          {modalData?.total_registered_users || 0}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-flashlight-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>
                            Puntos totales del rango
                          </span>
                        </div>

                        <span style={{ color: "#FFF", fontWeight: 500 }}>
                          {modalData?.last_rank?.total_points.toLocaleString()}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-award-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>
                            Rango alcanzado
                          </span>
                        </div>

                        <span
                          style={{
                            color: getRankColor(
                              modalData?.last_rank?.rank?.name,
                            ),
                            fontWeight: 700,
                          }}
                        >
                          {modalData?.last_rank?.rank?.name || "Sin Rango"}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <i
                            className="ri-calendar-check-line"
                            style={{
                              color: "#98A2B3",
                              fontSize: 16,
                            }}
                          />
                          <span style={{ color: "#98A2B3" }}>
                            Fecha de rango
                          </span>
                        </div>

                        <span style={{ color: "#FFF", fontWeight: 500 }}>
                          {modalData?.last_rank
                            ? new Date(
                                modalData?.last_rank?.period?.end ?? "",
                              ).toLocaleDateString()
                            : "Sin fecha"}
                        </span>
                      </div>
                    </div>
                  </>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Card>
    </>
  );
};

export default NetworkVisualization;
